import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export interface Discount {
  _id: string;
  code: string;
  discountType: 'flat' | 'percent';
  value: number;
  maxUsage: number | null;
  maxLimit: number | null;
  usedBy: string[];
  expiresAt: string | null;
  firstTimeUserOnly: boolean;
  minOrderValue: number | null;
  attachedTo: {
    name: string;
    totalBonus: number | null;
    value: number;
    bonusType: 'flat' | 'percent';
    minBonus: number | null;
  };
  isActive: boolean;
  isPublic: boolean;
  termsAndConditions?: string[];
  allowMultipleUse: boolean;
  forInstant?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountData {
  code: string;
  discountType: 'flat' | 'percent';
  value: number;
  maxUsage?: number | null;
  maxLimit?: number | null;
  expiresAt?: string | null;
  firstTimeUserOnly?: boolean;
  minOrderValue?: number | null;
  attachedTo: {
    name: string;
    totalBonus?: number | null;
    value: number;
    bonusType: 'flat' | 'percent';
    minBonus?: number | null;
  };
  isActive?: boolean;
  isPublic?: boolean;
  termsAndConditions?: string[];
  allowMultipleUse?: boolean;
  forInstant?: boolean;
}

export interface UpdateDiscountData extends Partial<CreateDiscountData> {}

export interface ApplyDiscountData {
  code: string;
  amount: number;
  userId: string;
}

export interface ValidateDiscountData {
  code: string;
  amount?: number;
  userId?: string;
}

const discountApi = {
  // Get all discounts
  getDiscounts: async (filters: any = {}) => {
    const { search, status, page = 1, limit = 20 } = filters;

    let url = `${API_URL}/api/admin/discounts?page=${page}&limit=${limit}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    if (status && status !== 'all') {
      url += `&status=${status}`;
    }

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
      },
    });

    return response.data;
  },

  // Get single discount by ID
  getDiscount: async (discountId: string) => {
    const response = await axios.get(`${API_URL}/api/admin/discounts/${discountId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
      },
    });

    return response.data;
  },

  // Create new discount
  createDiscount: async (discountData: CreateDiscountData) => {
    const response = await axios.post(
      `${API_URL}/api/admin/discounts`,
      discountData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  },

  // Update existing discount
  updateDiscount: async (discountId: string, discountData: UpdateDiscountData) => {
    const response = await axios.put(
      `${API_URL}/api/admin/discounts/${discountId}`,
      discountData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  },

  // Activate discount
  activateDiscount: async (discountId: string) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/discounts/${discountId}/activate`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  // Deactivate discount
  deactivateDiscount: async (discountId: string) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/discounts/${discountId}/inactivate`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  // Delete discount
  deleteDiscount: async (discountId: string) => {
    const response = await axios.delete(
      `${API_URL}/api/admin/discounts/${discountId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  // Apply discount to order
  applyDiscount: async (discountData: ApplyDiscountData) => {
    const response = await axios.post(
      `${API_URL}/discounts/apply`,
      discountData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  },

  // Validate discount code
  validateDiscount: async (discountData: ValidateDiscountData) => {
    const response = await axios.post(
      `${API_URL}/discounts/validate`,
      discountData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  },
};

export default discountApi;
